import { useState, useEffect, useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useOrgsApi } from 'src/api/db/apiOrgs'
import { useToast } from 'src/shared/hooks/use-toast'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { cleanUrl } from 'src/shared/utils/helpers'
import {
  generateBrandfetchUrls,
  isValidCompanyUrl,
  extractRootDomainName,
} from 'src/pages/LoginSignup/utils'
import { queryKeys } from 'src/api/db/queryKeys'

export const useExtractCompanyInfo = (organizationId?: string) => {
  const { t } = useLocalization()
  const { toast } = useToast()
  const { updateOrganization } = useOrgsApi({ org_id: organizationId })
  const queryClient = useQueryClient()

  const [companyUrl, setCompanyUrl] = useState('')
  const [debouncedUrl, setDebouncedUrl] = useState('')
  const [loading, setLoading] = useState(false)

  // Debounce URL input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedUrl(companyUrl)
    }, 500)

    return () => clearTimeout(timer)
  }, [companyUrl])

  const imgUrls = useMemo(() => {
    if (!debouncedUrl) return []
    const cleanedUrl = cleanUrl(debouncedUrl)
    return generateBrandfetchUrls(cleanedUrl)
  }, [debouncedUrl])

  const isUrlValid = isValidCompanyUrl(debouncedUrl)

  const handleExtractCompanyInfo = async () => {
    if (!organizationId || !debouncedUrl || !isUrlValid) return

    try {
      setLoading(true)
      const cleanedUrl = cleanUrl(debouncedUrl)
      const companyName = extractRootDomainName(cleanedUrl)
      const selectedLogoUrl = imgUrls[0] || null

      await updateOrganization.mutateAsync({
        id: organizationId,
        name: companyName,
        logo_urls: selectedLogoUrl,
        company_url: cleanedUrl,
        chosen_logo_url: selectedLogoUrl,
      })

      await queryClient.invalidateQueries({
        queryKey: [queryKeys.organization],
      })

      toast({
        variant: 'success',
        title: t('Company info updated', 'Företagsinformation uppdaterad'),
        description: t(
          'Company information has been extracted and updated.',
          'Företagsinformation har extraherats och uppdaterats.'
        ),
      })

      setCompanyUrl('')
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : t('Unknown error occurred', 'Ett okänt fel uppstod')
      toast({
        variant: 'destructive',
        title: t('Something went wrong', 'Något gick fel'),
        description: errorMessage,
      })
    } finally {
      setLoading(false)
    }
  }

  return {
    companyUrl,
    setCompanyUrl,
    isUrlValid,
    loading,
    handleExtractCompanyInfo,
  }
}
